import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import type {
  LightStudyGroupResponse,
  PaginatedResponse,
} from "@/lib/humhub-api/types";

type UsePaginatedStudyGroupsOptions = {
  initialPage?: number;
  initialPerPage?: number;
  queryKey: string;
  queryFn: (
    page: number,
    perPage: number,
  ) => Promise<PaginatedResponse<LightStudyGroupResponse>>;
  staleTime?: number;
  enabled?: boolean;
};

export const usePaginatedStudyGroups = ({
  initialPage = 1,
  initialPerPage = 20,
  queryKey,
  queryFn,
  staleTime = 3 * 60 * 1000,
  enabled = true,
}: UsePaginatedStudyGroupsOptions) => {
  const [page, setPage] = useState<number>(initialPage);
  const [perPage, setPerPage] = useState<number>(initialPerPage);

  const {
    data,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [queryKey, { page, perPage }],
    queryFn: () => queryFn(page, perPage),
    staleTime,
    enabled,
  });

  const pagination = useMemo(
    () =>
      data
        ? {
            total: data.total,
            page,
            perPage,
            pages: data.pages,
            links: data.links,
          }
        : undefined,
    [data, page, perPage],
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePerPageChange = useCallback((newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  }, []);

  return {
    data: data?.results || [],
    pagination,
    isLoading,
    error: queryError ? String(queryError) : null,
    page,
    perPage,
    handlePageChange,
    handlePerPageChange,
    refetch: () => refetch(),
  };
};
